import { Navigate, Outlet, RouteObject, createBrowserRouter } from "react-router-dom"
import { lazy, Suspense } from "react"
import { EpisodeSourceType } from "~/__generated__/graphql"
import {
  RequireSystemAdminSignedIn,
  RequireUserSignedIn,
  RequireUserSignedOut,
} from "~/auth/auth-layouts"
import { LoginLayout } from "~/auth/login-layout"
import {
  adminAdminUsersPath,
  adminAuraProductsPath,
  adminAuraCashOutPath,
  adminAuraCashOutHistoryPath,
  adminAdSponsorsPath,
  adminAdCampaignsPath,
  adminAdCampaignPath,
  adminDashboardPath,
  adminCreateDemoRoomPath,
  adminEditRoomPath,
  adminEditUserPath,
  adminUserEarningsPath,
  adminLoginPath,
  adminMessageModerationPath,
  adminUserModerationPath,
  adminNewAdminUserPath,
  adminRoomPath,
  adminRoomVipsPath,
  adminRoomBansPath,
  adminRoomsPath,
  adminAuraStickersPath,
  adminNewAuraStickerPackPath,
  adminAuraStickerPackPath,
  adminEditAuraStickerPackPath,
  adminSubscriptionProductsPath,
  adminUserDetailPath,
  adminUsersPath,
  adminMessageDetailPath,
  comingSoonPath,
  downloadPath,
  emailAuthPath,
  gamePath,
  gameLiPath,
  gameBbPath,
  invitePath,
  royalePath,
  landingPath,
  privacyPath,
  profilePath,
  rootPath,
  shareMessageCommentPath,
  shareMessageCardPath,
  shareMessagePath,
  shareRoomCardPath,
  shareRoomPath,
  shareUserProfilePath,
  shareLiveSessionPath,
  shareEpisodePath,
  supportPath,
  termsPath,
  adminRoomSubscriptionsPath,
  adminRoomHostStatsPath,
  adminCacheStatsPath,
  adminAiScoringPath,
  adminRankingPath,
  adminLoadSheddingStatsPath,
  adminNewMemberStatsPath,
  adminPredictionsPath,
  adminRambleMessagesNewPath,
  adminRambleMessagesPath,
  adminRoomTriviaPath,
  adminCreateRoomTriviaPath,
  adminEarningsPlansPath,
  adminEarningsPlanPath,
  adminEarningsEventsPath,
  adminEditEarningsPlanPath,
  adminCreateEarningsPlanPath,
  adminCreatePredictionGroupPath,
  adminUpdatePredictionGroupPath,
  adminInputPredictionResultsPath,
  sharePostCommentPath,
  sharePostPath,
  adminRoomTopMessagesPath,
  adminRoomEventsPath,
  adminCreateRoomEventPath,
  adminEditRoomEventPath,
  adminRoomLiveSessionsPath,
  adminRoomSimulcastsPath,
  adminCreateRoomSimulcastPath,
  adminEditRoomSimulcastPath,
  adminRoomSimulcastPath,
  adminRoomScheduledMessagesPath,
  adminRoomEpisodesPath,
  adminRoomContentSuggestionsPath,
  adminEpisodePath,
} from "~/common/paths"
import { ComingSoonScreen } from "~/landing/coming-soon-screen"
import { GameLiScreen } from "~/landing/game-li-screen"
import { GameBbScreen } from "~/landing/game-bb-screen"
import { LandingScreen } from "~/landing/landing-screen"
import { SupportScreen } from "~/landing/support-screen"
import { SidebarLayout } from "~/layouts/sidebar-layout"
import { EmailAuthScreen } from "~/login/email-auth-screen"
import { EmailLoginScreen } from "~/login/email-login-screen"
import { ProfileScreen } from "~/profile/profile-screen"
import { InviteScreen } from "~/share/invite-screen"
import { RoyaleScreen } from "~/share/royale-screen"
import { ShareMessageCardScreen } from "~/share/message-card-screen"
import { ShareMessageScreen } from "~/share/message-screen"
import { ShareRoomCardScreen } from "~/share/room-card-screen"
import { ShareRoomScreen } from "~/share/room-screen"
import { ShareUserProfileScreen } from "~/share/user-profile-screen"
import { DownloadScreen } from "../landing/download-screen"
import { ErrorBoundary } from "../ui/error-boundary"
import { RootLayout } from "./root-layout"

// Lazy load admin components
const PrivacyScreen = lazy(() =>
  import("~/landing/privacy-screen").then((m) => ({ default: m.PrivacyScreen }))
)
const TermsScreen = lazy(() =>
  import("~/landing/terms-screen").then((m) => ({ default: m.TermsScreen }))
)
const AdminAdminUsersScreen = lazy(() =>
  import("~/admin/admin-admin-users-screen").then((m) => ({ default: m.AdminAdminUsersScreen }))
)
const AdminAuraStickerPackFormScreen = lazy(() =>
  import("~/admin/admin-aura-stickers/admin-aura-sticker-pack-form-screen").then((m) => ({
    default: m.AdminAuraStickerPackFormScreen,
  }))
)
const AdminAuraStickerPackDetailScreen = lazy(() =>
  import("~/admin/admin-aura-stickers/admin-aura-sticker-pack-detail-screen").then((m) => ({
    default: m.AdminAuraStickerPackDetailScreen,
  }))
)
const AdminDashboardScreen = lazy(() =>
  import("~/admin/admin-dashboard-screen").then((m) => ({ default: m.AdminDashboardScreen }))
)
const AdminEditRoomScreen = lazy(() =>
  import("~/admin/admin-edit-room-screen/admin-edit-room-screen").then((m) => ({
    default: m.AdminEditRoomScreen,
  }))
)
const AdminEditUserScreen = lazy(() =>
  import("~/admin/admin-edit-user-screen/admin-edit-user-screen").then((m) => ({
    default: m.AdminEditUserScreen,
  }))
)
const AdminUserEarningsScreen = lazy(() =>
  import("~/admin/admin-user-earnings-screen").then((m) => ({
    default: m.AdminUserEarningsScreen,
  }))
)
const AdminNewUserAdminScreen = lazy(() =>
  import("~/admin/admin-new-admin-user-screen").then((m) => ({
    default: m.AdminNewUserAdminScreen,
  }))
)
const AdminRoomScreen = lazy(() =>
  import("~/admin/admin-room-screen/admin-room-screen").then((m) => ({
    default: m.AdminRoomScreen,
  }))
)
const AdminRoomVipsScreen = lazy(() =>
  import("~/admin/admin-room-vips-screen").then((m) => ({ default: m.AdminRoomVipsScreen }))
)
const AdminRoomBansScreen = lazy(() =>
  import("~/admin/admin-room-bans-screen").then((m) => ({ default: m.AdminRoomBansScreen }))
)
const AdminCreateDemoRoomScreen = lazy(() =>
  import("~/admin/admin-create-demo-room-screen/admin-create-demo-room-screen").then((m) => ({
    default: m.AdminCreateDemoRoomScreen,
  }))
)
const AdminRoomsScreen = lazy(() =>
  import("~/admin/admin-rooms-screen").then((m) => ({ default: m.AdminRoomsScreen }))
)
const AdminUserDetailScreen = lazy(() =>
  import("~/admin/admin-user-detail-screen/admin-user-detail-screen").then((m) => ({
    default: m.AdminUserDetailScreen,
  }))
)
const AdminUsersScreen = lazy(() =>
  import("~/admin/admin-users-screen").then((m) => ({ default: m.AdminUsersScreen }))
)
const AdminModerationScreen = lazy(() =>
  import("~/admin/admin-moderation-screen/admin-moderation-screen").then((m) => ({
    default: m.AdminModerationScreen,
  }))
)
const AdminRoomSubscriptionsScreen = lazy(() =>
  import("~/admin/admin-room-subscriptions-screen").then((m) => ({
    default: m.AdminRoomSubscriptionsScreen,
  }))
)
const AdminRoomHostStatsScreen = lazy(() =>
  import("~/admin/admin-room-host-stats-screen").then((m) => ({
    default: m.AdminRoomHostStatsScreen,
  }))
)
const AdminCacheStatsScreen = lazy(() =>
  import("~/admin/admin-cache-stats-screen").then((m) => ({
    default: m.AdminCacheStatsScreen,
  }))
)
const AdminLoadSheddingStatsScreen = lazy(() =>
  import("~/admin/admin-load-shedding-stats-screen").then((m) => ({
    default: m.AdminLoadSheddingStatsScreen,
  }))
)
const AdminNewMemberStatsScreen = lazy(() =>
  import("~/admin/admin-new-member-stats-screen").then((m) => ({
    default: m.AdminNewMemberStatsScreen,
  }))
)
const AdminRankingScreen = lazy(() =>
  import("~/admin/admin-ranking-screen").then((m) => ({
    default: m.AdminRankingScreen,
  }))
)
const AdminAiScoringScreen = lazy(() =>
  import("~/admin/admin-ai-scoring-screen").then((m) => ({
    default: m.AdminAiScoringScreen,
  }))
)
const AdminSubscriptionProductsScreen = lazy(() =>
  import("~/admin/admin-room-subscription-products-screen/admin-subscription-products-screen").then(
    (m) => ({ default: m.AdminSubscriptionProductsScreen })
  )
)
const AdminMessageDetailScreen = lazy(() =>
  import("~/admin/admin-message-detail-screen").then((m) => ({
    default: m.AdminMessageDetailScreen,
  }))
)
const AdminRambleMessagesScreen = lazy(() =>
  import("~/admin/admin-ramble-messages-screen/admin-ramble-messages-screen").then((m) => ({
    default: m.AdminRambleMessagesScreen,
  }))
)
const AdminRambleMessagesNewScreen = lazy(() =>
  import("~/admin/admin-ramble-messages-screen/admin-ramble-messages-new-screen").then((m) => ({
    default: m.AdminRambleMessagesNewScreen,
  }))
)
const AdminPredictionsScreen = lazy(() =>
  import("~/admin/predictions/admin-predictions-screen").then((m) => ({
    default: m.AdminPredictionsScreen,
  }))
)
const AdminEarningsPlansScreen = lazy(() =>
  import("~/admin/earnings/admin-earnings-plans-screen").then((m) => ({
    default: m.AdminEarningsPlansScreen,
  }))
)
const AdminCreateEarningsPlanScreen = lazy(() =>
  import("~/admin/earnings/admin-create-earnings-plan-screen").then((m) => ({
    default: m.AdminCreateEarningsPlanScreen,
  }))
)
const AdminEarningsPlanScreen = lazy(() =>
  import("~/admin/earnings/admin-earnings-plan-screen").then((m) => ({
    default: m.AdminEarningsPlanScreen,
  }))
)
const AdminEarningsEventsScreen = lazy(() =>
  import("~/admin/earnings/admin-earnings-events-screen").then((m) => ({
    default: m.AdminEarningsEventsScreen,
  }))
)
const AdminUpdateEarningsPlanScreen = lazy(() =>
  import("~/admin/earnings/admin-update-earnings-plan-screen").then((m) => ({
    default: m.AdminUpdateEarningsPlanScreen,
  }))
)
const AdminCreatePredictionGroupScreen = lazy(() =>
  import("~/admin/predictions/admin-create-prediction-group-screen").then((m) => ({
    default: m.AdminCreatePredictionGroupScreen,
  }))
)
const AdminUpdatePredictionGroupScreen = lazy(() =>
  import("~/admin/predictions/admin-update-prediction-group-screen").then((m) => ({
    default: m.AdminUpdatePredictionGroupScreen,
  }))
)
const AdminInputResultsScreen = lazy(() =>
  import("~/admin/predictions/admin-input-results-screen").then((m) => ({
    default: m.AdminInputResultsScreen,
  }))
)
const AdminAuraScreen = lazy(() =>
  import("~/admin/admin-aura-screen/admin-aura-screen").then((m) => ({
    default: m.AdminAuraScreen,
  }))
)
const AdminAuraCashOutHistoryScreen = lazy(() =>
  import("~/admin/admin-aura-screen/admin-aura-cash-out-history-screen").then((m) => ({
    default: m.AdminAuraCashOutHistoryScreen,
  }))
)
const AdminAdsScreen = lazy(() =>
  import("~/admin/admin-ads-screen/admin-ads-screen").then((m) => ({
    default: m.AdminAdsScreen,
  }))
)
const AdminAdCampaignDetailScreen = lazy(() =>
  import("~/admin/admin-ads-screen/admin-ad-campaign-detail-screen").then((m) => ({
    default: m.AdminAdCampaignDetailScreen,
  }))
)
const AdminRoomTopMessagesScreen = lazy(() =>
  import("~/admin/admin-room-top-messages-screen/admin-room-top-messages-screen").then((m) => ({
    default: m.AdminRoomTopMessagesScreen,
  }))
)
const AdminRoomEventsScreen = lazy(() =>
  import("~/admin/admin-room-events-screen/admin-room-events-screen").then((m) => ({
    default: m.AdminRoomEventsScreen,
  }))
)
const AdminRoomScheduledMessagesScreen = lazy(() =>
  import("~/admin/admin-room-scheduled-messages-screen/admin-room-scheduled-messages-screen").then(
    (m) => ({ default: m.AdminRoomScheduledMessagesScreen })
  )
)
const AdminRoomEpisodesScreen = lazy(() =>
  import("~/admin/admin-room-episodes-screen/admin-room-episodes-screen").then((m) => ({
    default: m.AdminRoomEpisodesScreen,
  }))
)
const AdminRoomEpisodesListTab = lazy(() =>
  import("~/admin/admin-room-episodes-screen/admin-room-episodes-list-tab").then((m) => ({
    default: m.AdminRoomEpisodesListTab,
  }))
)
const AdminRoomEpisodesSourcesTab = lazy(() =>
  import("~/admin/admin-room-episodes-screen/admin-room-episodes-sources-tab").then((m) => ({
    default: m.AdminRoomEpisodesSourcesTab,
  }))
)
const AdminRoomContentSuggestionsScreen = lazy(() =>
  import(
    "~/admin/admin-room-content-suggestions-screen/admin-room-content-suggestions-screen"
  ).then((m) => ({
    default: m.AdminRoomContentSuggestionsScreen,
  }))
)
const AdminRoomTriviaScreen = lazy(() =>
  import("~/admin/admin-room-trivia-screen/admin-room-trivia-screen").then((m) => ({
    default: m.AdminRoomTriviaScreen,
  }))
)
const AdminRoomTriviaCreateScreen = lazy(() =>
  import("~/admin/admin-room-trivia-screen/admin-room-trivia-create-screen").then((m) => ({
    default: m.AdminRoomTriviaCreateScreen,
  }))
)
const AdminEpisodeScreen = lazy(() =>
  import("~/admin/admin-episode-screen/admin-episode-screen").then((m) => ({
    default: m.AdminEpisodeScreen,
  }))
)
const AdminEpisodeTranscriptTab = lazy(() =>
  import("~/admin/admin-episode-screen/admin-episode-transcript-tab").then((m) => ({
    default: m.AdminEpisodeTranscriptTab,
  }))
)
const AdminEpisodeClipsTab = lazy(() =>
  import("~/admin/admin-episode-screen/admin-episode-clips-tab").then((m) => ({
    default: m.AdminEpisodeClipsTab,
  }))
)
const AdminEpisodeContentSuggestionsTab = lazy(() =>
  import("~/admin/admin-episode-screen/admin-episode-content-suggestions-tab").then((m) => ({
    default: m.AdminEpisodeContentSuggestionsTab,
  }))
)
const AdminEventFormScreen = lazy(() =>
  import("~/admin/admin-event-form-screen/admin-event-form-screen").then((m) => ({
    default: m.AdminEventFormScreen,
  }))
)

const AdminEventsCreateScreen = lazy(() =>
  import("~/admin/admin-events-create-screen/admin-events-create-screen").then((m) => ({
    default: m.AdminEventsCreateScreen,
  }))
)

const AdminRoomLiveSessionsScreen = lazy(() =>
  import("~/admin/admin-room-live-sessions-screen/admin-room-live-sessions-screen").then((m) => ({
    default: m.AdminRoomLiveSessionsScreen,
  }))
)

const AdminRoomSimulcastsScreen = lazy(() =>
  import("~/admin/admin-room-simulcasts-screen/admin-room-simulcasts-screen").then((m) => ({
    default: m.AdminRoomSimulcastsScreen,
  }))
)

const AdminSimulcastDetailScreen = lazy(() =>
  import("~/admin/admin-simulcast-detail-screen/admin-simulcast-detail-screen").then((m) => ({
    default: m.AdminSimulcastDetailScreen,
  }))
)

const AdminSimulcastFormScreen = lazy(() =>
  import("~/admin/admin-simulcast-form-screen/admin-simulcast-form-screen").then((m) => ({
    default: m.AdminSimulcastFormScreen,
  }))
)

// Loading component for Suspense fallbacks
const LoadingComponent = () => (
  <div className="flex items-center justify-center p-8">
    <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900"></div>
  </div>
)

const systemAdminAuthenticatedRoutes: Array<RouteObject> = [
  {
    path: adminDashboardPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminDashboardScreen />
      </Suspense>
    ),
  },
  {
    path: adminAdminUsersPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminAdminUsersScreen />
      </Suspense>
    ),
  },
  {
    path: adminMessageModerationPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminModerationScreen />
      </Suspense>
    ),
  },
  {
    path: adminUserModerationPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminModerationScreen />
      </Suspense>
    ),
  },
  {
    path: adminNewAdminUserPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminNewUserAdminScreen />
      </Suspense>
    ),
  },
  {
    path: adminUsersPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminUsersScreen />
      </Suspense>
    ),
  },
  {
    path: adminEditUserPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminEditUserScreen />
      </Suspense>
    ),
  },
  {
    path: adminUserEarningsPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminUserEarningsScreen />
      </Suspense>
    ),
  },
  {
    path: adminUserDetailPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminUserDetailScreen />
      </Suspense>
    ),
  },
  {
    path: adminRoomsPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminRoomsScreen />
      </Suspense>
    ),
  },
  {
    path: adminCreateDemoRoomPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminCreateDemoRoomScreen />
      </Suspense>
    ),
  },
  {
    path: adminRoomPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminRoomScreen />
      </Suspense>
    ),
  },
  {
    path: adminRoomVipsPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminRoomVipsScreen />
      </Suspense>
    ),
  },
  {
    path: adminRoomBansPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminRoomBansScreen />
      </Suspense>
    ),
  },
  {
    path: adminSubscriptionProductsPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminSubscriptionProductsScreen />
      </Suspense>
    ),
  },
  {
    path: adminRoomSubscriptionsPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminRoomSubscriptionsScreen />
      </Suspense>
    ),
  },
  {
    path: adminRoomHostStatsPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminRoomHostStatsScreen />
      </Suspense>
    ),
  },
  {
    path: adminEditRoomPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminEditRoomScreen />
      </Suspense>
    ),
  },
  {
    path: adminMessageDetailPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminMessageDetailScreen />
      </Suspense>
    ),
  },
  {
    path: adminCacheStatsPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminCacheStatsScreen />
      </Suspense>
    ),
  },
  {
    path: adminRankingPath.pattern,
    handle: { fullWidth: true },
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminRankingScreen />
      </Suspense>
    ),
  },
  {
    path: adminAiScoringPath.pattern,
    handle: { fullWidth: true },
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminAiScoringScreen />
      </Suspense>
    ),
  },
  {
    path: adminLoadSheddingStatsPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminLoadSheddingStatsScreen />
      </Suspense>
    ),
  },
  {
    path: adminNewMemberStatsPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminNewMemberStatsScreen />
      </Suspense>
    ),
  },
  {
    path: adminRambleMessagesPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminRambleMessagesScreen />
      </Suspense>
    ),
  },
  {
    path: adminRambleMessagesNewPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminRambleMessagesNewScreen />
      </Suspense>
    ),
  },
  {
    path: adminPredictionsPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminPredictionsScreen />
      </Suspense>
    ),
  },
  {
    path: adminEarningsPlansPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminEarningsPlansScreen />
      </Suspense>
    ),
  },
  {
    path: adminEditEarningsPlanPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminUpdateEarningsPlanScreen />
      </Suspense>
    ),
  },
  {
    path: adminEarningsEventsPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminEarningsEventsScreen />
      </Suspense>
    ),
  },
  {
    path: adminEarningsPlanPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminEarningsPlanScreen />
      </Suspense>
    ),
  },
  {
    path: adminCreateEarningsPlanPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminCreateEarningsPlanScreen />
      </Suspense>
    ),
  },
  {
    path: adminCreatePredictionGroupPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminCreatePredictionGroupScreen />
      </Suspense>
    ),
  },
  {
    path: adminUpdatePredictionGroupPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminUpdatePredictionGroupScreen />
      </Suspense>
    ),
  },
  {
    path: adminInputPredictionResultsPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminInputResultsScreen />
      </Suspense>
    ),
  },
  {
    path: adminAuraStickersPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminAuraScreen />
      </Suspense>
    ),
  },
  {
    path: adminNewAuraStickerPackPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminAuraStickerPackFormScreen />
      </Suspense>
    ),
  },
  {
    path: adminAuraStickerPackPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminAuraStickerPackDetailScreen />
      </Suspense>
    ),
  },
  {
    path: adminEditAuraStickerPackPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminAuraStickerPackFormScreen />
      </Suspense>
    ),
  },
  {
    path: adminAuraProductsPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminAuraScreen />
      </Suspense>
    ),
  },
  {
    path: adminAuraCashOutPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminAuraScreen />
      </Suspense>
    ),
  },
  {
    path: adminAuraCashOutHistoryPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminAuraCashOutHistoryScreen />
      </Suspense>
    ),
  },
  {
    path: adminAdSponsorsPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminAdsScreen />
      </Suspense>
    ),
  },
  {
    path: adminAdCampaignsPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminAdsScreen />
      </Suspense>
    ),
  },
  {
    path: adminAdCampaignPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminAdCampaignDetailScreen />
      </Suspense>
    ),
  },
  {
    path: adminRoomTopMessagesPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminRoomTopMessagesScreen />
      </Suspense>
    ),
  },
  {
    path: adminRoomEventsPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminRoomEventsScreen />
      </Suspense>
    ),
  },
  {
    path: adminRoomEpisodesPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminRoomEpisodesScreen />
      </Suspense>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="list" replace />,
      },
      {
        path: "list",
        element: (
          <Suspense fallback={<LoadingComponent />}>
            <AdminRoomEpisodesListTab sourceType={EpisodeSourceType.Youtube} />
          </Suspense>
        ),
      },
      {
        path: "live",
        element: (
          <Suspense fallback={<LoadingComponent />}>
            <AdminRoomEpisodesListTab sourceType={EpisodeSourceType.LiveSession} />
          </Suspense>
        ),
      },
      {
        path: "sources",
        element: (
          <Suspense fallback={<LoadingComponent />}>
            <AdminRoomEpisodesSourcesTab />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: adminRoomContentSuggestionsPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminRoomContentSuggestionsScreen />
      </Suspense>
    ),
  },
  {
    path: adminRoomTriviaPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminRoomTriviaScreen />
      </Suspense>
    ),
  },
  {
    path: adminCreateRoomTriviaPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminRoomTriviaCreateScreen />
      </Suspense>
    ),
  },
  {
    path: adminEpisodePath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminEpisodeScreen />
      </Suspense>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="transcript" replace />,
      },
      {
        path: "transcript",
        element: (
          <Suspense fallback={<LoadingComponent />}>
            <AdminEpisodeTranscriptTab />
          </Suspense>
        ),
      },
      {
        path: "clips",
        element: (
          <Suspense fallback={<LoadingComponent />}>
            <AdminEpisodeClipsTab />
          </Suspense>
        ),
      },
      {
        path: ":category",
        element: (
          <Suspense fallback={<LoadingComponent />}>
            <AdminEpisodeContentSuggestionsTab />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: adminCreateRoomEventPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminEventsCreateScreen />
      </Suspense>
    ),
  },
  {
    path: adminEditRoomEventPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminEventFormScreen />
      </Suspense>
    ),
  },
  {
    path: adminRoomLiveSessionsPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminRoomLiveSessionsScreen />
      </Suspense>
    ),
  },
  {
    path: adminRoomSimulcastsPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminRoomSimulcastsScreen />
      </Suspense>
    ),
  },
  {
    path: adminCreateRoomSimulcastPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminSimulcastFormScreen />
      </Suspense>
    ),
  },
  {
    path: adminRoomSimulcastPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminSimulcastDetailScreen />
      </Suspense>
    ),
  },
  {
    path: adminEditRoomSimulcastPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminSimulcastFormScreen />
      </Suspense>
    ),
  },
  {
    path: adminRoomScheduledMessagesPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminRoomScheduledMessagesScreen />
      </Suspense>
    ),
  },
]

const authenticatedRoutes: Array<RouteObject> = [
  {
    path: profilePath.pattern,
    element: <ProfileScreen />,
  },
]

export const router = createBrowserRouter([
  {
    path: "/",
    element: <RootLayout />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        element: <RequireSystemAdminSignedIn />,
        children: [
          {
            element: <SidebarLayout />,
            children: [
              {
                element: <Outlet />,
                children: [...systemAdminAuthenticatedRoutes],
              },
            ],
          },
        ],
      },
      {
        element: <RequireUserSignedIn />,
        children: [
          {
            element: <SidebarLayout />,
            children: [
              {
                element: <Outlet />,
                children: [...authenticatedRoutes],
              },
            ],
          },
        ],
      },
      {
        element: <RequireUserSignedOut />,
        children: [
          {
            element: <LoginLayout />,
            children: [
              {
                path: adminLoginPath.pattern,
                element: <EmailLoginScreen />,
              },
              // {
              //   path: adminCredentialsLoginPath.pattern,
              //   element: <CredentialsLoginScreen />,
              // },
              {
                path: emailAuthPath.pattern,
                element: <EmailAuthScreen />,
              },
            ],
          },
        ],
      },
      {
        path: downloadPath.pattern,
        element: <DownloadScreen />,
      },
      {
        path: landingPath.pattern,
        element: <LandingScreen />,
      },
      {
        path: invitePath.pattern,
        element: <InviteScreen />,
      },
      {
        path: royalePath.pattern,
        element: <RoyaleScreen />,
      },
      {
        path: shareLiveSessionPath.pattern,
        element: <ShareMessageScreen />,
      },
      {
        path: shareEpisodePath.pattern,
        element: <ShareMessageScreen />,
      },
      {
        path: shareUserProfilePath.pattern,
        element: <ShareUserProfileScreen />,
      },
      {
        path: shareRoomPath.pattern,
        element: <ShareRoomScreen />,
      },
      {
        path: shareRoomCardPath.pattern,
        element: <ShareRoomCardScreen />,
      },
      {
        path: shareMessagePath.pattern,
        element: <ShareMessageScreen />,
      },
      {
        path: shareMessageCardPath.pattern,
        element: <ShareMessageCardScreen />,
      },
      {
        path: shareMessageCommentPath.pattern,
        element: <ShareMessageScreen />,
      },
      {
        path: sharePostCommentPath.pattern,
        element: <ShareMessageScreen />,
      },
      {
        path: sharePostPath.pattern,
        element: <ShareMessageScreen />,
      },
      {
        path: comingSoonPath.pattern,
        element: <ComingSoonScreen />,
      },
      {
        path: privacyPath.pattern,
        element: (
          <Suspense fallback={<LoadingComponent />}>
            <PrivacyScreen />
          </Suspense>
        ),
      },
      {
        path: termsPath.pattern,
        element: (
          <Suspense fallback={<LoadingComponent />}>
            <TermsScreen />
          </Suspense>
        ),
      },
      {
        path: supportPath.pattern,
        element: <SupportScreen />,
      },
      {
        path: gamePath.pattern,
        element: <Navigate to={gameLiPath.pattern} replace />,
      },
      {
        path: gameLiPath.pattern,
        element: <GameLiScreen />,
      },
      {
        path: gameBbPath.pattern,
        element: <GameBbScreen />,
      },
      {
        path: rootPath.pattern,
        element: <LandingScreen />,
      },
    ],
  },
])
